import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { AutocompleteCustomAdaptor } from './autocomplete-custom-adaptor';
import { Observable, of } from 'rxjs';
import { AutoCompleteComponent, AutoCompleteModule } from '@syncfusion/ej2-angular-dropdowns';
import { MultiSelectItem } from './multi-select-item';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    imports: [AutoCompleteModule, TranslateModule]
})
export class AutocompleteComponent implements OnInit, AfterViewInit {

  @ViewChild('autocomplete')
  public autocomplete: AutoCompleteComponent;

  @ViewChild('autocomplete', { read: ElementRef, static: false })
  public autocompleteElement: ElementRef; // Reference to the underlying DOM element

  @Input()
  public getDataFunction: (searchText: string) => Observable<string[] | MultiSelectItem[]>;

  @Input()
  public placeholder = 'i18n.shared.search';

  @Input()
  public set value(value: any) {
    if (this._outputValue !== value) {
      // set values to 'null' to force reset with data reload
      this._outputValue = value;
      if (this._outputValue) {
        this.autocomplete?.refresh();
      }
    }
  }

  @Input()
  // eslint-disable-next-line @typescript-eslint/ban-types
  public fields: Object;

  @Input()
  public enabled = true;

  @Input()
  public set isFormComponent(value: boolean) {
    if (this._isFormComponent === value) {
      return;
    }
    this._isFormComponent = value;
    this.updateCssClass();
  }

  @Input()
  public set invalid(value: boolean) {
    if (this._invalid === value) {
      return;
    }
    this._invalid = value;
    this.updateCssClass();
  }

  @Input()
  public allowObjectBinding: boolean = false; // set to true if you use a list of objects

  @Output()
  public valueChange: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public touched: EventEmitter<void> = new EventEmitter();

  public get outputValue() {
    return this._outputValue;
  }

  public set outputValue(value: any) {
    if (this._outputValue !== value) {
      this._outputValue = value;
      this.valueChange.emit(this._outputValue);
    }
  }

  public data: DataManager;
  public isFirst = false;
  public cssClass = null;
  private _invalid = false;
  private _isFormComponent = false;

  private _outputValue: any;

  constructor() {
  }

  public onSelect(data: any) {
    const value = data.itemData?.id ?? data.itemData ?? null;
    if (this._outputValue != value) {
      this._outputValue = value;
      this.valueChange.emit(this._outputValue);
    }
  }

  public onChange(args: any) {
    let value = args.itemData?.id ?? args.itemData ?? null;
    if (isNaN(value)) {
      value = null;
    }
    if (value != this._outputValue) {
      this._outputValue = value;
      this.valueChange.emit(this._outputValue);
    }
  }

  ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AutocompleteCustomAdaptor(this.getDataFunctionWrapped.bind(this)),
    });

    if (this.isFormComponent) {
      this.cssClass = 'form-autocomplete';
    }
  }

  ngAfterViewInit() {
    //Detect when clear icon gets clicked
    this.autocomplete.element.addEventListener('input', e => {
      if (!this.isFirst) {
        this.isFirst = true;
        this.autocomplete.element.parentElement
          .querySelector('.e-clear-icon')
          .addEventListener('mousedown', args => {
            this.valueChange.emit();
          });
      }
    });
  }

  public refresh() {
    this.autocomplete.refresh();
  }

  public onBlur() {
    this.touched.emit();
    if (!this._outputValue && this.autocomplete.value) {
      setTimeout(() => {
        this.autocomplete.value = this._outputValue;
      }, 0);
    }
  }

  public focus(): void {
    // Access the input element of the Syncfusion AutoComplete component
    const inputElement = this.autocompleteElement?.nativeElement.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }

  private getDataFunctionWrapped(searchText: string): Observable<string[] | MultiSelectItem[]> {
    if (!this.getDataFunction) {
      return of([]);
    }
    return this.getDataFunction(searchText);
  }

  private updateCssClass() {
    this.cssClass = '';
    if (this._isFormComponent) {
      this.cssClass = 'form-autocomplete';
    }
    if (this._invalid) {
      this.cssClass += ' e-error';
    }
  }
}
